/**
 * The home strip's two numbers: what today added, what it took away.
 *
 * Pure, and tested without a device. The rule mirrors the server's
 * todayTotals (apps/server/src/routes/records.ts) line for line: only money
 * records play, the currency is the first non-null one among them, a record
 * carrying another currency is left out rather than added to a total that
 * would then be in no currency at all, and `out` is a positive number.
 *
 * "Money record" locally means `direction` set in record_catalog — the server
 * nulls it exactly when a record measures nothing or points no way.
 */
import { dayInZone, type RecordListToday } from '@alluzo/shared';

/** One catalog row, narrowed to what the currency pick reads. */
export type TodayCatalogRow = {
  readonly currency: string | null;
  readonly direction: string | null;
};

/**
 * The currency today's totals are in, or null when no money record carries
 * one. First non-null among money records, in catalog order — the server's
 * rule, and one value in practice because every record a template seeds
 * carries the tenant's currency.
 */
export const pickTodayCurrency = (catalog: readonly TodayCatalogRow[]): string | null =>
  catalog.find((row) => row.direction !== null && row.currency !== null)?.currency ?? null;

/**
 * Fold one day's signed values into the strip. Nulls are rows that move no
 * total (no measure, or no sign chosen yet) and are skipped, matching the
 * server's signedValue. Zero counts as in, as it does there.
 */
export const toTodayTotals = (
  currency: string | null,
  signedValues: readonly (number | null)[],
): RecordListToday => {
  if (currency === null) return { currency: null, in: 0, out: 0 };
  let moneyIn = 0;
  let moneyOut = 0;
  for (const value of signedValues) {
    if (value === null) continue;
    if (value >= 0) moneyIn += value;
    else moneyOut -= value;
  }
  return { currency, in: moneyIn, out: moneyOut };
};

/** One of today's rows, as the strip reads it: its signed value and how it arrived. */
export type TodayRow = {
  readonly signedValue: number | null;
  readonly sourceChannel: string | null;
};

/**
 * The signed values today's totals add, with the rows a link posted left out.
 *
 * Money is counted once (Q5 of docs/plans/qa-fixes-1.md): a ₹40 cash sale and
 * the copy its link wrote into Cash are one ₹40, not "₹80 in today". The
 * server's todayTotals leaves the same rows out, so the strip and the catalogue
 * still agree.
 */
export const countedOnce = (rows: readonly TodayRow[]): (number | null)[] =>
  rows.filter((row) => row.sourceChannel !== 'link').map((row) => row.signedValue);

/**
 * The workspace's time zone, as an IANA name, or null when nothing will say.
 *
 * "Today" is the workspace's own day (R2 of docs/plans/qa-fixes-2.md): a sale
 * at 01:55 IST on 18 Oct is today on 18 Oct, though UTC still calls it the
 * 17th. A workspace has no stored zone yet (no tenant column), so this is the
 * person's device zone, the fallback R2 allows where the workspace has none.
 * It is also the zone the entry form's "today" default is written in
 * (`localClock` in component-default.ts), so the day a row is dated and the
 * day the screens call today are one day. When a workspace zone is stored,
 * this is the one place that reads it.
 */
export const workspaceZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
};

export { dayInZone } from '@alluzo/shared';

/** The day an instant (a row's `createdAt`, a sync stamp) falls on in the workspace. */
export const workspaceDayOf = (instant: Date, zone: string | null = workspaceZone()): string =>
  dayInZone(instant, zone);

/**
 * Today, as every number and word a person reads asks it: the workspace's day.
 *
 * The day column (`entry_mirror.day`, `dayOf` in entry-format.ts) holds a
 * `date` field's own calendar day, the day the person picked, and an instant
 * (a datetime axis, or `createdAt`) read in this same zone. Asking it with the UTC day
 * was the defect: from 00:00 to 05:30 IST "today" was yesterday, and a sale
 * dated today counted nowhere.
 */
export const workspaceToday = (now: Date = new Date(), zone: string | null = workspaceZone()): string =>
  dayInZone(now, zone);

/**
 * The UTC day, for the mirror's own bookkeeping only: how far back a first
 * download asks and what a prune keeps (mirror-window.ts). A day either way
 * there moves nothing a person reads, and the server counts its window in UTC.
 * Never for a "today" a person sees: that is `workspaceToday`.
 */
export const utcToday = (): string => new Date().toISOString().slice(0, 10);
